import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type FpaVarianceCategory, type VarianceBaselineMeta } from '../lib/api';
import { Card, CardHeader, Select, ActionButton, Spinner, EmptyState, Modal } from '../components/ui';
import { fmtCurrencyWhole, fmtMonth, fmtDateTime } from '../lib/fmt';
import { VarianceTrendChart } from '../components/FpaCharts';
import { StatementView } from '../components/FpaStatement';
import { AnswerBand, VarianceWaterfall } from '../components/FpaReadouts';
import { summarizeVariance } from '../lib/fpaVarianceSummary';
import { lineCodeFamily } from '../lib/fpaLineCodes';

// Drill-modal labels/sign (the income-statement grid itself is now StatementView).
const CATEGORY_LABEL: Record<FpaVarianceCategory, string> = {
  revenue: 'Revenue', 'cost.variable': 'Variable cost', 'cost.salary': 'Salaries & wages',
  'cost.fixed': 'Fixed cost', gross_margin: 'Gross profit', operating_profit: 'Operating profit',
};
const CATEGORY_SIGN: Record<FpaVarianceCategory, 1 | -1> = {
  revenue: 1, 'cost.variable': -1, 'cost.salary': -1, 'cost.fixed': -1, gross_margin: 1, operating_profit: 1,
};
const monthInput: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};
function defaultMonths() {
  const d = new Date();
  const to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const s = new Date(d.getFullYear(), d.getMonth() - 11, 1);
  return { from: `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}`, to };
}

export default function AdminFpaVariancePage() {
  const init = defaultMonths();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [baselineId, setBaselineId] = useState('');
  const [drill, setDrill] = useState<{ category: FpaVarianceCategory; period: string } | null>(null);

  const plansQ = useQuery(() => api.fpa.plans.list());
  // Trend chart + baseline meta come from the variance report; the GRID is the
  // unified statement (Actual + the chosen baseline → Δ), shared with Plan/Actuals.
  const { data } = useQuery(() => api.fpa.variance({ from, to, baselines: baselineId ? [baselineId] : [] }), [from, to, baselineId]);
  const stmt = useQuery(() => api.fpa.statement({ from, to, plan: baselineId ? [baselineId] : undefined, includeActual: true }), [from, to, baselineId]);
  const depts = useQuery(() => api.fpa.departments());
  const deptNames = Object.fromEntries((depts.data?.departments ?? []).map((d) => [d.id, d.name]));

  const primary: VarianceBaselineMeta | undefined = data?.baselines.find((b) => b.id === baselineId) ?? data?.baselines[0];
  const months = data?.periods ?? [];
  // Answer-first summary + waterfall: a single month → that month, a range → its total.
  const summary = data && months.length ? summarizeVariance(data, primary?.id ?? null, months.length === 1 ? months[0] : undefined) : null;
  const periodLabel = months.length === 0 ? '' : months.length === 1 ? fmtMonth(months[0]!) : `${fmtMonth(months[0]!)}–${fmtMonth(months[months.length - 1]!)}`;

  return (
    <>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>FP&amp;A variance</div>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <Select value={baselineId} onChange={setBaselineId}>
              <option value="">Baseline: auto (budget + forecast)</option>
              {(plansQ.data?.plans ?? []).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.kind})</option>)}
            </Select>
            <input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={monthInput} />
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>→</span>
            <input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={monthInput} />
            <ActionButton onClick={() => api.fpa.exportVariance({ from, to, baselines: baselineId ? [baselineId] : [] })}>Export CSV</ActionButton>
          </span>
        </CardHeader>
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>
          Actual vs {primary ? <strong>{primary.name}</strong> : 'baseline'}, laid out as an income statement. Δ is sign-adjusted so green is favourable to operating profit; click any figure to drill into its contributors.
          {stmt.data?.freshness.last_synced_at ? <> Actuals synced {fmtDateTime(stmt.data.freshness.last_synced_at)}.</> : <> No QBO sync yet.</>}
        </div>
        {summary && <div style={{ padding: '0 16px 8px' }}><AnswerBand summary={summary} baselineName={primary?.name ?? 'baseline'} periodLabel={periodLabel} /></div>}
        {stmt.isLoading ? <Spinner />
          : stmt.error ? <EmptyState message={stmt.error} />
          : !stmt.data || stmt.data.rows.length === 0 ? <EmptyState message="No data for this range." />
          : (
            <StatementView
              statement={stmt.data}
              deptNames={deptNames}
              onCellClick={(line, month) => { const cat = lineCodeFamily(line); if (cat) setDrill({ category: cat, period: month }); }}
            />
          )}
      </Card>

      {summary?.hasBaseline && <VarianceWaterfall summary={summary} baselineName={primary?.name ?? 'baseline'} />}
      {data && months.length > 0 && <VarianceTrendChart rows={data.rows} periods={months} primaryId={primary?.id ?? null} />}

      {drill && (
        <DrillModal
          category={drill.category}
          period={drill.period}
          baselineId={primary?.id ?? null}
          onClose={() => setDrill(null)}
        />
      )}
    </>
  );
}

function DrillModal({ category, period, baselineId, onClose }: { category: FpaVarianceCategory; period: string; baselineId: string | null; onClose: () => void }) {
  const { user } = useAuth();
  const canWrite = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver';
  const contrib = useQuery(() => api.fpa.varianceContributions(category, period, baselineId), [category, period, baselineId]);
  const notesQ = useQuery(() => api.fpa.varianceNotes({ plan: baselineId, category, period }), [category, period, baselineId]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function addNote() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.fpa.addVarianceNote({ plan_version_id: baselineId, category, period_month: period, body });
      setBody(''); notesQ.refetch();
    } finally { setBusy(false); }
  }

  return (
    <Modal title={`${CATEGORY_LABEL[category]} · ${fmtMonth(period)}`} subtitle="Contributors & commentary" onClose={onClose} width={760}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!contrib.isLoading && contrib.data && (() => {
          const actualTotal = contrib.data.actual.reduce((s, c) => s + c.amount, 0);
          const planTotal = contrib.data.plan.reduce((s, c) => s + c.amount, 0);
          const d = Math.round((actualTotal - planTotal) * 100) / 100;
          const favorable = CATEGORY_SIGN[category] * d;
          const dColor = favorable > 0 ? 'var(--color-green-text)' : favorable < 0 ? 'var(--color-red-text)' : 'var(--color-text-hint)';
          return (
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, padding: '8px 12px', background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border)' }}>
              <span><span style={{ color: 'var(--color-text-muted)' }}>Actual</span> <strong>{fmtCurrencyWhole(actualTotal)}</strong></span>
              <span><span style={{ color: 'var(--color-text-muted)' }}>Plan</span> <strong>{fmtCurrencyWhole(planTotal)}</strong></span>
              <span><span style={{ color: 'var(--color-text-muted)' }}>Δ (actual − plan)</span> <strong style={{ color: dColor }}>{d > 0 ? '+' : ''}{fmtCurrencyWhole(d)}</strong></span>
            </div>
          );
        })()}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <ContribList title="Actual (QBO)" items={contrib.data?.actual ?? []} loading={contrib.isLoading} />
          <ContribList title="Plan" items={contrib.data?.plan ?? []} loading={contrib.isLoading} />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Commentary</div>
          {notesQ.isLoading ? <Spinner />
            : (notesQ.data?.notes ?? []).length === 0 ? <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No notes yet.</div>
            : (notesQ.data!.notes.map((n) => (
              <div key={n.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '0.5px solid var(--color-border)' }}>
                <div>{n.body}</div>
                <div style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>{n.author_id} · {new Date(n.created_at).toLocaleString()}</div>
              </div>
            )))}
          {canWrite && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note…" style={{ ...monthInput, flex: 1 }} />
              <ActionButton variant="primary" disabled={busy || !body.trim()} onClick={addNote}>Add</ActionButton>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ContribList({ title, items, loading }: { title: string; items: Array<{ key: string; label: string; amount: number }>; loading: boolean }) {
  const total = items.reduce((s, c) => s + c.amount, 0);
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span>{title}</span>
        {!loading && items.length > 0 && <span>{fmtCurrencyWhole(total)}</span>}
      </div>
      {loading ? <Spinner />
        : items.length === 0 ? <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>—</div>
        : (
          <>
            {items.map((c) => (
              <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '0.5px solid var(--color-border)' }}>
                <span>{c.label}</span><span>{fmtCurrencyWhole(c.amount)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, padding: '6px 0' }}>
              <span>Total</span><span>{fmtCurrencyWhole(total)}</span>
            </div>
          </>
        )}
    </div>
  );
}
